// frontend/src/components/ComplaintEdit.js
import React, { useState, useEffect } from 'react';
import api from '../../services/api'; // Use your custom API instance
import { useNavigate, useParams } from 'react-router-dom';
import {
  Container,
  Box,
  Typography,
  TextField,
  MenuItem,
  Grid,
  FormControl,
  InputLabel,
  Select,
  FormHelperText,
  Paper,
  IconButton,
  Button,
  Tooltip,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Alert,
  CircularProgress
} from '@mui/material';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';

import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SaveIcon from '@mui/icons-material/Save';
import UploadFileIcon from '@mui/icons-material/UploadFile';

// Get base URL for protected file endpoints (same as ComplaintDetails.js)
const baseURL = process.env.NODE_ENV === 'production' 
  ? 'https://api.kcvenugopal.online/api'
  : (process.env.REACT_APP_API_URL || 'http://localhost:5000/api');

const ComplaintEdit = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  // For category dropdown
  const [category, setCategory] = useState([]);
  // For dynamic legislative assemblies from DB
  const [assemblies, setAssemblies] = useState([]);
  // For dynamic districts from DB
  const [districts, setDistricts] = useState([]);

  // Form state (pre-populated)
  const [formData, setFormData] = useState({
    name: '',
    parentName: '',
    legislativeAssembly: '',
    district: '',
    address: '',
    panchayath: '',
    wardNo: '',
    mobileNumber: '',
    alternateMobileNumber: '',
    category: '',
    idProofType: '',
    idNumber: '',
    reference: '',
    referredMobileNumber: '',
    subject: '',
    complaintDetails: '',
    remarks: '',
    status: '',
    priority: 'Medium',
    complaintId: ''
  });

  // File state for optional file updates
  const [idProofFile, setIdProofFile] = useState(null);
  const [complaintPdfFile, setComplaintPdfFile] = useState(null);

  // Preview state (for the right column)
  const [idProofPreview, setIdProofPreview] = useState(null);
  const [complaintPdfPreview, setComplaintPdfPreview] = useState(null);

  const [errors, setErrors] = useState({});
  const [openErrorDialog, setOpenErrorDialog] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5 MB

  // Fetch categories, complaint data, and assemblies on mount
  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch categories
        const categoryResponse = await api.get('/category');
        setCategory(categoryResponse.data);
      } catch (error) {
        console.error('Error fetching categories:', error);
      }

      try {
        // Let the API interceptor handle the Authorization header automatically
        const complaintResponse = await api.get(`/complaints/${id}`);
        const data = complaintResponse.data;
        
        // Convert ObjectId fields to strings if they're objects
        const formDataToSet = {
          ...data,
          district: data.district ? (typeof data.district === 'object' ? data.district._id || data.district.toString() : data.district.toString()) : '',
          legislativeAssembly: data.legislativeAssembly ? (typeof data.legislativeAssembly === 'object' ? data.legislativeAssembly._id || data.legislativeAssembly.toString() : data.legislativeAssembly.toString()) : ''
        };
        
        setFormData(formDataToSet);

        // Helper function to validate file path
        const isValidFilePath = (path) => {
          return path && 
                 path !== null && 
                 path !== 'null' && 
                 path !== undefined && 
                 path !== 'undefined' &&
                 typeof path === 'string' &&
                 path.trim() !== '';
        };

        // Set PDF previews using protected file endpoints (same as ComplaintDetails.js)
        if (isValidFilePath(data.idProofFilePath)) {
          // Use protected endpoint: /api/files/:complaintId/id-proof
          // Include token in query parameter for iframe compatibility
          const token = localStorage.getItem('token');
          const url = `${baseURL}/files/${data.complaintId}/id-proof${token ? `?token=${encodeURIComponent(token)}` : ''}`;
          setIdProofPreview(url);
          console.log('🆔 ID Proof PDF URL (protected):', url);
          console.log('🆔 Complaint ID:', data.complaintId);
          // Verify URL is correct format
          if (url.includes('/uploads/')) {
            console.error('❌ ERROR: URL contains /uploads/ - should use /api/files/ instead!');
          }
        } else if (data.idProofFileData && data.idProofFileContentType) {
          const uint8Array = new Uint8Array(data.idProofFileData.data);
          const blob = new Blob([uint8Array], { type: data.idProofFileContentType });
          setIdProofPreview(URL.createObjectURL(blob));
        } else {
          setIdProofPreview(null);
          console.log('🆔 ID Proof PDF path is null or invalid, skipping URL construction');
        }
        
        if (isValidFilePath(data.complaintPdfFilePath)) {
          // Use protected endpoint: /api/files/:complaintId/complaint-pdf
          // Include token in query parameter for iframe compatibility
          const token = localStorage.getItem('token');
          const url = `${baseURL}/files/${data.complaintId}/complaint-pdf${token ? `?token=${encodeURIComponent(token)}` : ''}`;
          setComplaintPdfPreview(url);
          console.log('📄 Complaint PDF URL (protected):', url);
          console.log('📄 Complaint ID:', data.complaintId);
          // Verify URL is correct format
          if (url.includes('/uploads/')) {
            console.error('❌ ERROR: URL contains /uploads/ - should use /api/files/ instead!');
          }
        } else if (data.complaintPdfFileData && data.complaintPdfFileContentType) {
          const uint8Array = new Uint8Array(data.complaintPdfFileData.data);
          const blob = new Blob([uint8Array], { type: data.complaintPdfFileContentType });
          setComplaintPdfPreview(URL.createObjectURL(blob));
        } else {
          setComplaintPdfPreview(null);
          console.log('📄 Complaint PDF path is null or invalid, skipping URL construction');
        }
      } catch (error) {
        console.error('Error fetching complaint:', error);
      }

      try {
        const assembliesResponse = await api.get('/assemblies');
        setAssemblies(assembliesResponse.data);
      } catch (error) {
        console.error('Error fetching assemblies:', error);
      }

      try {
        const districtsResponse = await api.get('/districts');
        // Ensure response.data is an array
        if (Array.isArray(districtsResponse.data)) {
          setDistricts(districtsResponse.data);
        } else {
          console.warn('Districts API returned non-array data:', districtsResponse.data);
          setDistricts([]);
        }
      } catch (error) {
        console.error('Error fetching districts:', error);
        // Ensure districts remains an empty array on error
        setDistricts([]);
      }
    };

    fetchData();
  }, [id]);

  // Cleanup generated Blob URLs on unmount
  useEffect(() => {
    return () => {
      if (idProofPreview) URL.revokeObjectURL(idProofPreview);
      if (complaintPdfPreview) URL.revokeObjectURL(complaintPdfPreview);
    };
  }, [idProofPreview, complaintPdfPreview]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  // When assembly changes, clear panchayath as options will change
  const handleAssemblyChange = (e) => {
    const value = e.target.value;
    setFormData(prev => ({
      ...prev,
      legislativeAssembly: value,
      panchayath: ''
    }));
  };

  const handleComplaintDetailsChange = (value) => {
    setFormData(prev => ({ ...prev, complaintDetails: value }));
  };

  const handleFileChange = (e) => {
    const { name, files } = e.target;
    if (files.length > 0) {
      const file = files[0];
      if (file.type !== 'application/pdf') {
        setErrors(prev => ({ ...prev, [name]: 'File must be a PDF' }));
        return;
      }
      if (file.size > MAX_FILE_SIZE) {
        setErrors(prev => ({ ...prev, [name]: 'File size must be less than 5 MB' }));
        return;
      }
      setErrors(prev => ({ ...prev, [name]: '' }));
      if (name === 'idProofFile') {
        setIdProofFile(file);
        setIdProofPreview(URL.createObjectURL(file));
      } else if (name === 'complaintPdfFile') {
        setComplaintPdfFile(file);
        setComplaintPdfPreview(URL.createObjectURL(file));
      }
    }
  };

  // Helper function to combine error messages
  const buildErrorMessage = (errorsObj) => {
    return Object.entries(errorsObj)
      .map(([field, msg]) => `${field}: ${msg}`)
      .join('\n');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    let formErrors = {};
    // Define required fields (removed optional fields: parentName, address, wardNo, mobileNumber, idProofType, idNumber, reference)
    const requiredFields = [
      'name',
      'legislativeAssembly',
      'panchayath',
      'category',
      'subject',
      'complaintDetails',
      'status'
    ];
    requiredFields.forEach((key) => {
      if (!formData[key]) {
        formErrors[key] = 'This field is required';
      }
    });
    if (Object.keys(formErrors).length > 0) {
      setErrorMessage(buildErrorMessage(formErrors));
      setOpenErrorDialog(true);
      return;
    }

    if (isSubmitting) return; // guard against duplicate submits
    setIsSubmitting(true);
    try {
      // Let the API interceptor handle the Authorization header automatically
      // IMPORTANT: only send the editable fields. Appending the whole fetched
      // document would push _id, __v, createdAt, file-path/buffer fields, etc.
      // into the multipart body as "[object Object]" and corrupt the record.
      const editableFields = [
        'name', 'parentName', 'legislativeAssembly', 'district', 'address',
        'panchayath', 'wardNo', 'mobileNumber', 'alternateMobileNumber',
        'category', 'idProofType', 'idNumber', 'reference', 'referredMobileNumber',
        'subject', 'complaintDetails', 'remarks', 'status', 'priority'
      ];
      const data = new FormData();
      editableFields.forEach((key) => {
        const value = formData[key];
        if (value !== undefined && value !== null) {
          data.append(key, value);
        }
      });
      if (idProofFile) data.append('idProofFile', idProofFile);
      if (complaintPdfFile) data.append('complaintPdfFile', complaintPdfFile);

      await api.put(`/complaints/${id}`, data, {
        headers: {
          // Don't set Content-Type for FormData - browser will set it with boundary
          // Authorization header is automatically added by the API interceptor
          'x-complaint-id': formData.complaintId
        }
      });
      alert('Complaint updated successfully!');
      // Go back where the edit was opened from rather than to a bare
      // /complaints: the caller may be Pending, Resolved or My Assigned, and a
      // list carries its search and filters in the query string. Same as the
      // Cancel button below.
      navigate(-1);
    } catch (error) {
      console.error('Error updating complaint:', error);
      const backendError = error.response?.data?.error || error.message;
      setErrorMessage(backendError);
      setOpenErrorDialog(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCloseErrorDialog = () => {
    setOpenErrorDialog(false);
    setErrorMessage('');
  };

  // Determine selected assembly for panchayath options
  const selectedAssembly = assemblies.find(
    (assembly) => assembly._id === formData.legislativeAssembly
  );
  
  // Note: If you are encountering X-Frame-Options issues because your PDF files are served with that header,
  // you may want to avoid embedding them and instead provide a button to open them in a new tab.
  const handlePrintPdf = (pdfUrl) => {
    if (pdfUrl) {
      window.open(pdfUrl, '_blank');
    }
  };

  // Convert assembly id to name for display (if available)
  const assemblyObj = assemblies.find(
    (a) => a._id.toString() === formData.legislativeAssembly?.toString()
  );
  const assemblyName = assemblyObj ? assemblyObj.name : formData.legislativeAssembly;

  return (
    <Container maxWidth="lg" sx={{ mt: 2, mb: 4, px: 2, mx: 'auto' }}>
      {/* Inline styles added for ReactQuill customization */}
      <style>{`
        .custom-quill .ql-container {
          min-height: 300px;
        }
      `}</style>
      <Paper
        elevation={4}
        sx={{
          p: 4,
          backgroundColor: '#fff',
          boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
          borderRadius: '8px'
        }}
      >
        <form onSubmit={handleSubmit}>
          {/* Top Action Bar */}
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
            <Tooltip title="Go Back">
              <IconButton color="primary" onClick={() => navigate(-1)}>
                <ArrowBackIcon />
              </IconButton>
            </Tooltip>
            <Typography
              variant="h5"
              sx={{
                flexGrow: 1,
                textAlign: 'center',
                fontWeight: 600,
                fontFamily: 'Roboto, sans-serif',
                color: '#333'
              }}
            >
              Edit Complaint {formData.complaintId}
            </Typography>
            <Tooltip title="Save Changes">
              <span>
                <IconButton color="primary" type="submit" disabled={isSubmitting} aria-label="Save changes">
                  {isSubmitting ? <CircularProgress size={20} /> : <SaveIcon />}
                </IconButton>
              </span>
            </Tooltip>
          </Box>

          <Grid container spacing={3}>
            {/* Left Column: Form Fields */}
            <Grid item xs={12} md={8}>
              <Grid container spacing={3}>
                {/* Division 1: Personal Information */}
                <Grid item xs={12}>
                  <Typography variant="h6" sx={{ color: '#333', fontFamily: 'Roboto, sans-serif' }}>
                    Personal Information
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Name"
                    name="name"
                    value={formData.name || ''}
                    onChange={handleChange}
                    required
                    variant="outlined"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Name of Father/Mother (Optional)"
                    name="parentName"
                    value={formData.parentName || ''}
                    onChange={handleChange}
                    variant="outlined"
                  />
                </Grid>

                {/* Division 2: Location & Contact Information */}
                <Grid item xs={12}>
                  <Typography variant="h6" sx={{ color: '#333', fontFamily: 'Roboto, sans-serif' }}>
                    Location & Contact Information
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth required variant="outlined">
                    <InputLabel>Legislative Assembly</InputLabel>
                    <Select
                      label="Legislative Assembly"
                      name="legislativeAssembly"
                      value={formData.legislativeAssembly || ''}
                      onChange={handleAssemblyChange}
                    >
                      <MenuItem value="">Select Assembly</MenuItem>
                      {assemblies.map((assembly) => (
                        <MenuItem key={assembly._id} value={assembly._id}>
                          {assembly.name}
                        </MenuItem>
                      ))}
                    </Select>
                    <FormHelperText>{errors.legislativeAssembly}</FormHelperText>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth variant="outlined">
                    <InputLabel>District (Optional)</InputLabel>
                    <Select
                      label="District (Optional)"
                      name="district"
                      value={formData.district ? String(formData.district) : ''}
                      onChange={handleChange}
                    >
                      <MenuItem value="">Select District</MenuItem>
                      {Array.isArray(districts) && districts.map((district) => (
                        <MenuItem key={district._id} value={String(district._id)}>
                          {district.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Address (Optional)"
                    name="address"
                    value={formData.address || ''}
                    onChange={handleChange}
                    variant="outlined"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth required variant="outlined">
                    <InputLabel>Panchayath</InputLabel>
                    <Select
                      label="Panchayath"
                      name="panchayath"
                      value={formData.panchayath || ''}
                      onChange={handleChange}
                      disabled={!formData.legislativeAssembly}
                    >
                      <MenuItem value="">Select Panchayath</MenuItem>
                      {selectedAssembly &&
                        selectedAssembly.municipalities.map((muni, index) => (
                          <MenuItem key={index} value={muni.name}>
                            {muni.name}
                          </MenuItem>
                        ))}
                    </Select>
                    <FormHelperText>{errors.panchayath}</FormHelperText>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Ward No (Optional)"
                    name="wardNo"
                    value={formData.wardNo || ''}
                    onChange={handleChange}
                    variant="outlined"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Mobile Number (Optional)"
                    name="mobileNumber"
                    value={formData.mobileNumber || ''}
                    onChange={handleChange}
                    variant="outlined"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Alternate Mobile Number"
                    name="alternateMobileNumber"
                    value={formData.alternateMobileNumber || ''}
                    onChange={handleChange}
                    variant="outlined"
                  />
                </Grid>

                {/* Division 3: Complaint & ID Details */}
                <Grid item xs={12}>
                  <Typography variant="h6" sx={{ color: '#333', fontFamily: 'Roboto, sans-serif' }}>
                    Complaint & ID Details
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth required variant="outlined">
                    <InputLabel>Category</InputLabel>
                    <Select
                      label="Category"
                      name="category"
                      value={formData.category || ''}
                      onChange={handleChange}
                    >
                      <MenuItem value="">Select Category</MenuItem>
                      {category.map((cat) => (
                        <MenuItem key={cat._id} value={cat.name}>
                          {cat.name}
                        </MenuItem>
                      ))}
                    </Select>
                    <FormHelperText>{errors.category}</FormHelperText>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth required variant="outlined">
                    <InputLabel>Status</InputLabel>
                    <Select
                      label="Status"
                      name="status"
                      value={formData.status || ''}
                      onChange={handleChange}
                    >
                      <MenuItem value="">Select Status</MenuItem>
                      <MenuItem value="New">New</MenuItem>
                      <MenuItem value="Open">Open</MenuItem>
                      <MenuItem value="Pending">Pending</MenuItem>
                      <MenuItem value="Solved">Solved</MenuItem>
                      <MenuItem value="Resolved">Resolved</MenuItem>
                    </Select>
                    <FormHelperText>{errors.status}</FormHelperText>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth variant="outlined">
                    <InputLabel>Priority</InputLabel>
                    <Select
                      label="Priority"
                      name="priority"
                      value={formData.priority || 'Medium'}
                      onChange={handleChange}
                    >
                      <MenuItem value="Low">Low</MenuItem>
                      <MenuItem value="Medium">Medium</MenuItem>
                      <MenuItem value="High">High</MenuItem>
                      <MenuItem value="Urgent">Urgent</MenuItem>
                    </Select>
                    <FormHelperText>{errors.priority}</FormHelperText>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth variant="outlined">
                    <InputLabel>ID Proof Type (Optional)</InputLabel>
                    <Select
                      label="ID Proof Type (Optional)"
                      name="idProofType"
                      value={formData.idProofType || ''}
                      onChange={handleChange}
                    >
                      <MenuItem value="">Select ID Proof</MenuItem>
                      <MenuItem value="Aadhaar">Aadhaar</MenuItem>
                      <MenuItem value="DL">DL</MenuItem>
                      <MenuItem value="Election ID">Election ID</MenuItem>
                      <MenuItem value="Passport">Passport</MenuItem>
                      <MenuItem value="Other">Other</MenuItem>
                    </Select>
                    <FormHelperText>{errors.idProofType}</FormHelperText>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="ID Number (Optional)"
                    name="idNumber"
                    value={formData.idNumber || ''}
                    onChange={handleChange}
                    variant="outlined"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Reference (Optional)"
                    name="reference"
                    value={formData.reference || ''}
                    onChange={handleChange}
                    variant="outlined"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Referred Mobile Number"
                    name="referredMobileNumber"
                    value={formData.referredMobileNumber || ''}
                    onChange={handleChange}
                    variant="outlined"
                  />
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="h6" sx={{ color: '#333', fontFamily: 'Roboto, sans-serif' }}>
                    Complaint Subject
                  </Typography>
                  <TextField
                    fullWidth
                    label="Subject"
                    name="subject"
                    value={formData.subject || ''}
                    onChange={handleChange}
                    required
                    variant="outlined"
                  />
                </Grid>
              </Grid>
            </Grid>

            {/* Right Column: PDF Previews & Uploads */}
            <Grid item xs={12} md={4}>
              <Box sx={{ pl: 2 }}>
                {/* Complaint PDF Preview & Upload */}
                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle1" gutterBottom>
                    Complaint PDF Preview
                  </Typography>
                  {complaintPdfPreview ? (
                    <Box
                      sx={{
                        border: '1px solid #ccc',
                        borderRadius: '4px',
                        overflow: 'hidden',
                        boxShadow: 2
                      }}
                    >
                      <iframe
                        src={complaintPdfPreview}
                        title="Complaint PDF Preview"
                        width="100%"
                        height="400"
                        frameBorder="0"
                      />
                    </Box>
                  ) : (
                    <Box
                      sx={{
                        border: '1px dashed #ccc',
                        borderRadius: '4px',
                        p: 2,
                        textAlign: 'center',
                        boxShadow: 2
                      }}
                    >
                      <Typography variant="body2">No PDF uploaded yet.</Typography>
                    </Box>
                  )}
                  <Button
                    variant="contained"
                    component="label"
                    fullWidth
                    sx={{
                      mt: 1,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: 1,
                      '&:hover': { backgroundColor: 'var(--primary-color)' }
                    }}
                  >
                    <UploadFileIcon />
                    Upload Complaint (PDF)
                    <input
                      type="file"
                      name="complaintPdfFile"
                      accept="application/pdf"
                      hidden
                      onChange={handleFileChange}
                    />
                  </Button>
                  {complaintPdfPreview && (
                    <Button
                      variant="outlined"
                      fullWidth
                      sx={{ mt: 1 }}
                      onClick={() => handlePrintPdf(complaintPdfPreview)}
                    >
                      View PDF
                    </Button>
                  )}
                </Box>
                {/* ID Proof PDF Preview & Upload */}
                <Box>
                  <Typography variant="subtitle1" gutterBottom>
                    ID Proof PDF Preview
                  </Typography>
                  {idProofPreview ? (
                    <Box
                      sx={{
                        border: '1px solid #ccc',
                        borderRadius: '4px',
                        overflow: 'hidden',
                        boxShadow: 2
                      }}
                    >
                      <iframe
                        src={idProofPreview}
                        title="ID Proof PDF Preview"
                        width="100%"
                        height="400"
                        frameBorder="0"
                      />
                    </Box>
                  ) : (
                    <Box
                      sx={{
                        border: '1px dashed #ccc',
                        borderRadius: '4px',
                        p: 2,
                        textAlign: 'center',
                        boxShadow: 2
                      }}
                    >
                      <Typography variant="body2">No PDF uploaded yet.</Typography>
                    </Box>
                  )}
                  <Button
                    variant="contained"
                    component="label"
                    fullWidth
                    sx={{
                      mt: 1,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: 1,
                      '&:hover': { backgroundColor: 'var(--primary-color)' }
                    }}
                  >
                    <UploadFileIcon />
                    Upload ID Proof (PDF)
                    <input
                      type="file"
                      name="idProofFile"
                      accept="application/pdf"
                      hidden
                      onChange={handleFileChange}
                    />
                  </Button>
                  {idProofPreview && (
                    <Button
                      variant="outlined"
                      fullWidth
                      sx={{ mt: 1 }}
                      onClick={() => handlePrintPdf(idProofPreview)}
                    >
                      View PDF
                    </Button>
                  )}
                </Box>
              </Box>
            </Grid>
          </Grid>

          {/* Full-Width Complaint Details & Remarks */}
          <Grid container spacing={2} sx={{ mt: 3 }}>
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>
                Complaint Details
              </Typography>
              {/* ReactQuill with custom class to style only inner editor */}
              <ReactQuill
                theme="snow"
                value={formData.complaintDetails || ''}
                onChange={handleComplaintDetailsChange}
                className="custom-quill"
                style={{ marginBottom: '20px' }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Remarks"
                name="remarks"
                value={formData.remarks || ''}
                onChange={handleChange}
                multiline
                rows={3}
                variant="outlined"
              />
            </Grid>
            <Grid item xs={12}>
              <Box sx={{ mt: 3 }}>
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  fullWidth
                  disabled={isSubmitting}
                  startIcon={isSubmitting ? <CircularProgress size={18} color="inherit" /> : <SaveIcon />}
                  sx={{ py: 1.5 }}
                >
                  {isSubmitting ? 'Updating…' : 'Update Complaint'}
                </Button>
              </Box>
            </Grid>
          </Grid>
        </form>
      </Paper>

      {/* Error Dialog for validation/backend errors */}
      <Dialog
        open={openErrorDialog}
        onClose={handleCloseErrorDialog}
        aria-labelledby="error-dialog-title"
        aria-describedby="error-dialog-description"
      >
        <DialogTitle id="error-dialog-title">Validation Errors</DialogTitle>
        <DialogContent>
          <DialogContentText id="error-dialog-description" style={{ whiteSpace: 'pre-wrap' }}>
            {errorMessage}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseErrorDialog} color="primary">
            OK
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default ComplaintEdit;
